import { ReactNode, useLayoutEffect } from "react";
import { Alert } from "@mui/material";
import { usePageHeaderOverride } from "../../Layout/PageContainer";

// Shared manage-page scaffolding (platform UI/UX polish, Phase A). Every
// default below is a verbatim copy of what ManageMembers renders today —
// ManageMembers adopts with zero visual change; ManageInvoices / ManageEvents
// / ManageEmails adopt in Phase B via the escape-hatch props.

export type ManagePageStatusMessage = {
  severity: "success" | "info" | "error" | "warning";
  message: ReactNode;
};

// Field-for-field mirror of PageContainer's PageHeaderOverride so the object
// can be handed straight to usePageHeaderOverride's setter.
export type ManagePageHeaderOverride = {
  title?: string;
  description?: string;
  actions?: ReactNode;
  titleClassName?: string;
  descriptionClassName?: string;
  withHeaderDivider?: boolean;
};

export type ManagePageShellProps = {
  children: ReactNode;
  className?: string;
  // Pages MUST memoize this object (useMemo INSIDE the component) — the
  // header effect re-fires on every referential change and can loop via
  // PageContainer state updates. Do NOT hoist it to module scope if it
  // references RoutePaths or other enumStrings exports: enumStrings imports
  // the page components for the route registry, so module-init evaluation
  // hits the circular-import TDZ and white-screens the app on cold load
  // (bit ManageInvoices in the B4/B5 polish pass).
  headerOverride?: ManagePageHeaderOverride | null;
  statusMessage?: ManagePageStatusMessage | null;
  onStatusMessageClose?: () => void;
  statusMessageClassName?: string;
};

export const ManagePageShell = ({
  children,
  className = "w-full pb-10",
  headerOverride = null,
  statusMessage = null,
  onStatusMessageClose,
  statusMessageClassName = "mb-5",
}: ManagePageShellProps) => {
  const setPageHeaderOverride = usePageHeaderOverride();

  // useLayoutEffect (not useEffect) so the header override is installed
  // before first paint — avoids a one-frame flash of the default header
  // (e.g. Manage Invoices' 34px title + gold Create New Invoice button).
  useLayoutEffect(() => {
    setPageHeaderOverride(headerOverride ?? null);

    return () => setPageHeaderOverride(null);
  }, [headerOverride, setPageHeaderOverride]);

  return (
    <div className={className}>
      {statusMessage && (
        <Alert
          severity={statusMessage.severity}
          className={statusMessageClassName}
          onClose={onStatusMessageClose}
        >
          {statusMessage.message}
        </Alert>
      )}
      {children}
    </div>
  );
};

export type ManagePageSectionProps = {
  title: string;
  description?: string;
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  // Phase B: right-aligned action rows (e.g. export buttons on Manage Events
  // / Manage Emails). Unused by ManageMembers.
  actions?: ReactNode;
  // Phase B: header-row layout when actions are present. Default is the
  // plain <div> wrapper ManageMembers renders around its section headings.
  headerRowClassName?: string;
  children?: ReactNode;
};

export const ManagePageSection = ({
  title,
  description,
  className,
  titleClassName = "text-lg font-bold text-black",
  descriptionClassName = "mt-2 text-sm text-black",
  actions,
  headerRowClassName,
  children,
}: ManagePageSectionProps) => (
  <section className={className}>
    {actions ? (
      <div className={headerRowClassName}>
        <div>
          <h2 className={titleClassName}>{title}</h2>
          {description && <p className={descriptionClassName}>{description}</p>}
        </div>
        {actions}
      </div>
    ) : (
      <div className={headerRowClassName}>
        <h2 className={titleClassName}>{title}</h2>
        {description && <p className={descriptionClassName}>{description}</p>}
      </div>
    )}
    {children}
  </section>
);
